import React, { useEffect, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import jsPDF from "jspdf";
import AddNewTimeLog from "./AddNewTimeLog";
import Moment from "react-moment";
import AddEditNotes from "./AddEditNotes";
import CandidatePay from "./CandidatePay";
import {
  formatTotalHours,
  formatAmount,
  getFieldData,
  getMonthLastDay,
  getMonthName,
  getTotalDays,
  getTotalHours,
} from "../../Services/appService";
import { getAlert, getConfirmAlert } from "../../Services/appAlerts";
import {
  getCurrentUser,
  getLoggedUserDetails,
  loadUserDetails,
} from "../../app/store/auth";
import { useDispatch, useSelector } from "react-redux";
import {
  getProjectDateUpdateStatus,
  getAllRates,
  getTimeLogReportStatus,
  getTimeLogDuration,
  getTimeLogs,
  getTimeSheetDelStatus,
} from "../../app/store/timeLog";
import {
  getTimeLogInvoice,
} from "../../app/store/timeLogInvoice";
import useDocDownload from "../../app/hooks/useDocDownload";
import RateEncryptDecrypt from "../../Services/RateEncryptDecrypt";
import { getCandidateAppAllTasks } from "../../app/store/candidate";
import {
  useLoadTimeLogsQuery,
  useLoadTimeLogDurationQuery,
  useLoadAllRatesQuery,
  useLoadTimeLogInvoiceQuery,
  useUpdateProjectStartDateMutation,
  useDeleteTimeSheetDocMutation,
  useSetTimeLogReportStatusMutation,
} from "../../app/store/timeLogApiSlice";

const DOC_ICON_BY_EXTENSION = {
  pdf: "fa-file-pdf-o",
  doc: "fa-file-word-o", docx: "fa-file-word-o", rtf: "fa-file-word-o", odt: "fa-file-word-o",
  xls: "fa-file-excel-o", xlsx: "fa-file-excel-o", csv: "fa-file-excel-o", ods: "fa-file-excel-o",
  ppt: "fa-file-powerpoint-o", pptx: "fa-file-powerpoint-o", odp: "fa-file-powerpoint-o",
  png: "fa-file-image-o", jpg: "fa-file-image-o", jpeg: "fa-file-image-o", gif: "fa-file-image-o",
  bmp: "fa-file-image-o", webp: "fa-file-image-o", svg: "fa-file-image-o",
  tif: "fa-file-image-o", tiff: "fa-file-image-o", heic: "fa-file-image-o",
  zip: "fa-file-archive-o", rar: "fa-file-archive-o", "7z": "fa-file-archive-o",
  tar: "fa-file-archive-o", gz: "fa-file-archive-o",
  txt: "fa-file-text-o", log: "fa-file-text-o", md: "fa-file-text-o",
  mp3: "fa-file-audio-o", wav: "fa-file-audio-o", m4a: "fa-file-audio-o",
  mp4: "fa-file-video-o", mov: "fa-file-video-o", avi: "fa-file-video-o", mkv: "fa-file-video-o",
  html: "fa-file-code-o", xml: "fa-file-code-o", json: "fa-file-code-o"
};

const getDocumentIcon = (doc) => {
  const name = String(doc?.docName || doc?.documentName || doc || "").trim().toLowerCase();
  const ext = name.includes(".") ? name.slice(name.lastIndexOf(".") + 1) : "";
  if (DOC_ICON_BY_EXTENSION[ext]) return DOC_ICON_BY_EXTENSION[ext];

  const mime = String(doc?.docType || "").trim().toLowerCase();
  if (mime.startsWith("image/")) return "fa-file-image-o";
  if (mime.startsWith("audio/")) return "fa-file-audio-o";
  if (mime.startsWith("video/")) return "fa-file-video-o";
  if (mime.startsWith("text/")) return "fa-file-text-o";
  if (mime === "application/pdf") return "fa-file-pdf-o";
  if (mime.includes("word")) return "fa-file-word-o";
  if (mime.includes("sheet") || mime.includes("excel")) return "fa-file-excel-o";
  if (mime.includes("presentation") || mime.includes("powerpoint")) return "fa-file-powerpoint-o";
  if (mime.includes("zip") || mime.includes("compressed")) return "fa-file-archive-o";

  return "fa-file-text-o";
};

const getDocumentIconStyle = (doc) => {
  const icon = getDocumentIcon(doc);
  if (icon === "fa-file-pdf-o") return { color: "#dc2626" };
  if (icon === "fa-file-word-o") return { color: "#2563eb" };
  if (icon === "fa-file-excel-o") return { color: "#16a34a" };
  if (icon === "fa-file-image-o") return { color: "#9333ea" };
  if (icon === "fa-file-powerpoint-o") return { color: "#ea580c" };
  if (icon === "fa-file-archive-o") return { color: "#d97706" };
  return { color: "#2563eb" };
};

const TIME_LOG_STYLES = {
  card: {
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    backgroundColor: "#ffffff",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.04)",
    overflow: "hidden"
  },
  cardHeader: {
    padding: "18px 22px",
    borderBottom: "1px solid #e5e5e5",
    background: "#ffffff"
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "14px",
    flexWrap: "wrap"
  },
  titleWrap: {
    display: "inline-flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0
  },
  titleIcon: {
    width: "34px",
    height: "34px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "14px",
    border: "1px solid #e2e8f0",
    boxShadow: "none",
    flexShrink: 0
  },
  title: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: 1.25,
    color: "#000000",
    margin: 0
  },
  subtitle: {
    fontSize: "13px",
    lineHeight: 1.5,
    color: "#666666",
    margin: 0,
    fontWeight: "normal"
  },
  refreshButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    background: "#ffffff",
    color: "#333333",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #2563eb",
    background: "#2563eb",
    color: "#ffffff",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.2)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  cardBody: {
    padding: "0"
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "14px",
    padding: "20px 22px"
  },
  statCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    backgroundColor: "#ffffff",
    border: "1px solid #e5e5e5",
    borderRadius: "0px",
    padding: "16px",
    boxShadow: "0 6px 18px rgba(0, 0, 0, 0.03)",
    transition: "all 0.2s ease"
  },
  statIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "14px",
    flexShrink: 0
  },
  statContent: {
    minWidth: 0
  },
  statLabel: {
    margin: "0 0 4px 0",
    color: "#666666",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase"
  },
  statValue: {
    margin: 0,
    color: "#000000",
    fontSize: "24px",
    fontWeight: 600,
    lineHeight: 1
  },
  utilityRow: {
    padding: "0 22px 14px 22px"
  },
  utilityPanel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "14px",
    padding: "14px 18px",
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    background: "#f5f5f5",
    width: "100%",
    flexWrap: "wrap"
  },
  utilityPanelTop: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
    minWidth: 0
  },
  utilityIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    flexShrink: 0
  },
  utilityCopy: {
    minWidth: 0,
    flex: 1
  },
  utilityLabel: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#666666",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    margin: 0
  },
  utilityValue: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#000000",
    lineHeight: 1.2,
    marginTop: "2px"
  },
  utilityActions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap"
  },
  utilityEditor: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "nowrap",
    marginLeft: "auto"
  },
  utilityInput: {
    minWidth: "160px",
    maxWidth: "240px"
  },
  utilityButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    background: "#ffffff",
    color: "#333333",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
    cursor: "pointer"
  },
  utilityPrimaryButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #2563eb",
    background: "#2563eb",
    color: "#ffffff",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.2)",
    cursor: "pointer"
  },
  utilityDangerButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    background: "#ffffff",
    color: "#333333",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
    cursor: "pointer"
  },
  tableWrap: {
    overflowX: "auto",
    backgroundColor: "#ffffff",
    width: "100%"
  },
  table: {
    width: "100%",
    marginBottom: 0,
    fontSize: "13px",
    color: "#333333"
  },
  tableHeadCell: {
    borderTop: "1px solid #e5e5e5",
    borderBottom: "1px solid #e5e5e5",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#666666",
    background: "#f5f5f5",
    padding: "14px 16px",
    whiteSpace: "nowrap",
    verticalAlign: "top"
  },
  tableHeadSubtext: {
    display: "block",
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "none",
    color: "#94a3b8",
    marginTop: "4px"
  },
  cell: {
    verticalAlign: "top",
    padding: "14px 16px",
    borderBottom: "1px solid #e5e5e5",
    fontSize: "13px",
    color: "#333333",
    fontWeight: 600
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "34px",
    padding: "0 12px",
    borderRadius: "0px",
    background: "#f5f5f5",
    color: "#000000",
    fontSize: "13px",
    fontWeight: 600,
    border: "1px solid #e2e8f0",
    whiteSpace: "nowrap"
  },
  periodText: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "9px 18px",
    borderRadius: "0px",
    background: "#ffffff",
    border: "1px solid #e5e5e5",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
    fontSize: "13px",
    fontWeight: 600,
    color: "#333333",
    marginBottom: "0",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  periodIcon: {
    color: "#0f172a",
    fontSize: "13px"
  },
  metricButton: {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "3px",
    padding: "8px 14px",
    borderRadius: "0px",
    background: "#ffffff",
    border: "1px solid #e5e5e5",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
    color: "#333333",
    cursor: "pointer",
    textAlign: "left"
  },
  metricPrimary: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#000000"
  },
  metricSecondary: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#666666",
    textTransform: "uppercase",
    letterSpacing: "0.04em"
  },
  metricStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start"
  },
  statusStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "7px"
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    borderRadius: "0px",
    fontSize: "12.5px",
    fontWeight: 600,
    lineHeight: 1.2,
    whiteSpace: "nowrap"
  },
  statusPending: {
    color: "#b45309",
    backgroundColor: "#fffbeb",
    border: "1px solid rgba(253, 230, 138, 1)"
  ,
    fontWeight: 500
  },
  statusApproved: {
    color: "#166534",
    backgroundColor: "#f0fdf4",
    border: "1px solid rgba(187, 247, 208, 1)"
  },
  statusRejected: {
    color: "#991b1b",
    backgroundColor: "#fef2f2",
    border: "1px solid rgba(254, 202, 202, 1)"
  },
  statusReviewed: {
    color: "#000000",
    backgroundColor: "#f5f5f5",
    border: "1px solid #e2e8f0"
  },
  statusDate: {
    fontSize: "12px",
    color: "#666666",
    lineHeight: 1.4
  },
  docList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  docRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    padding: "8px 12px",
    border: "1px solid #e5e5e5",
    borderRadius: "0px",
    background: "#ffffff",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)"
  },
  docLink: {
    border: "none",
    background: "transparent",
    color: "#2563eb",
    padding: 0,
    fontSize: "13px",
    fontWeight: 600,
    textAlign: "left",
    wordBreak: "break-word"
  },
  iconButton: {
    width: "28px",
    height: "28px",
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    background: "#ffffff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)"
  },
  docUploadButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    background: "#ffffff",
    color: "#333333",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
    cursor: "pointer",
    whiteSpace: "nowrap"
  },
  notesButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    borderRadius: "0px",
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    color: "#000000",
    fontSize: "13px",
    fontWeight: 600,
    padding: "9px 18px",
    whiteSpace: "nowrap",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
    lineHeight: 1.2
  },
  notesBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "18px",
    height: "18px",
    borderRadius: "0px",
    backgroundColor: "#f1f5f9",
    color: "#0f172a",
    fontSize: "11px",
    fontWeight: 700,
    padding: "1px 5px",
    lineHeight: 1.2,
    border: "1px solid #cbd5e1",
    marginLeft: "4px"
  },
  actionLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    background: "#ffffff",
    color: "#333333",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
    cursor: "pointer"
  },
  actionStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "8px"
  },
  monthYearStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "2px"
  },
  monthYearPrimary: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#000000",
    textTransform: "capitalize",
    letterSpacing: "0.01em"
  },
  monthYearSecondary: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#94a3b8"
  },
  paymentBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    alignItems: "flex-start",
    minWidth: "160px"
  },
  paymentDocList: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%"
  },
  paymentDocButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    padding: "6px 10px",
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    background: "#ffffff",
    color: "#2563eb",
    fontSize: "12px",
    fontWeight: 600,
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
    cursor: "pointer",
    maxWidth: "200px",
    textAlign: "left",
    lineHeight: 1.2
  },
  paymentDocName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "130px"
  },
  paymentDocDownloadIcon: {
    fontSize: "11px",
    color: "#94a3b8",
    marginLeft: "auto",
    flexShrink: 0
  },
  paymentDateWrap: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#000000",
    lineHeight: 1.3,
    whiteSpace: "nowrap"
  },
  paymentMetricsList: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
    paddingTop: "2px"
  },
  paymentMetricRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12.5px",
    fontWeight: 600,
    color: "#333333"
  },
  paymentMetricLabel: {
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#666666",
    minWidth: "68px"
  },
  paymentMetricVal: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#000000"
  },
  perDiemStack: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    alignItems: "flex-start",
    minWidth: "140px"
  },
  perDiemRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12.5px",
    fontWeight: 600,
    color: "#333333"
  },
  perDiemLabel: {
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#666666",
    minWidth: "42px"
  },
  perDiemValue: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#000000"
  },
  emptyIndicator: {
    color: "#94a3b8",
    fontSize: "13px",
    fontWeight: 500
  },
  reportButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    background: "#ffffff",
    color: "#333333",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
    cursor: "pointer",
    whiteSpace: "nowrap"
  },
  reportGeneratedButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid rgba(187, 247, 208, 1)",
    background: "#f0fdf4",
    color: "#166534",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600
  },
  modalHeader: {
    padding: "18px 22px",
    borderBottom: "1px solid #e5e5e5",
    background: "#f5f5f5",
  },
  modalTitleWrap: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
  },
  modalIcon: {
    width: "42px",
    height: "42px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    border: "1px solid #e2e8f0",
    flexShrink: 0,
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: 800,
    color: "#000000",
    margin: 0,
  },
  modalSubtitle: {
    fontSize: "12px",
    color: "#666666",
    marginTop: "4px",
  },
  modalCloseButton: {
    width: "36px",
    height: "36px",
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    background: "#ffffff",
    color: "#666666",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: "auto",
    flexShrink: 0,
  },
  modalBody: {
    padding: "20px 22px",
    background: "#ffffff",
  },
  reportSummaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: "12px",
  },
  reportSummaryCard: {
    borderRadius: "0px",
    padding: "14px",
    background: "#ffffff",
    border: "1px solid #e5e5e5",
    boxShadow: "0 10px 22px rgba(15, 23, 42, 0.04)",
  },
  reportSummaryLabel: {
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#666666",
    marginBottom: "6px",
  },
  reportSummaryValue: {
    fontSize: "16px",
    fontWeight: 800,
    color: "#000000",
    lineHeight: 1.3,
  },
  reportPreviewCard: {
    marginTop: "16px",
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    background: "#ffffff",
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.05)",
    overflow: "hidden",
  },
  reportPreviewHeader: {
    padding: "16px 18px",
    borderBottom: "1px solid #e5e5e5",
    background: "#f5f5f5",
  },
  reportPreviewTitle: {
    margin: 0,
    color: "#000000",
    fontSize: "16px",
    fontWeight: 800,
  },
  reportPreviewSubtitle: {
    margin: "5px 0 0",
    color: "#666666",
    fontSize: "12px",
    lineHeight: 1.5,
  },
  reportMetricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "12px",
    padding: "18px",
  },
  reportMetricCard: {
    position: "relative",
    minHeight: "112px",
    border: "1px solid #e5e5e5",
    borderRadius: "0px",
    background: "#ffffff",
    padding: "16px 16px 14px 16px",
    overflow: "hidden",
    boxShadow: "0 10px 22px -18px rgba(15, 23, 42, 0.35)",
  },
  reportMetricAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
  },
  reportMetricIcon: {
    width: "34px",
    height: "34px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #e5e5e5",
    marginBottom: "12px",
  },
  reportMetricLabel: {
    marginBottom: "6px",
    color: "#666666",
    fontSize: "10px",
    fontWeight: 850,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
  },
  reportMetricValue: {
    color: "#000000",
    fontSize: "20px",
    fontWeight: 850,
    lineHeight: 1.15,
    wordBreak: "break-word",
  },
  reportMuted: {
    display: "block",
    marginTop: "4px",
    color: "#666666",
    fontSize: "11px",
    lineHeight: 1.35,
  },
  modalFooter: {
    padding: "16px 22px",
    borderTop: "1px solid #e5e5e5",
    background: "#ffffff",
  },
  modalFooterActions: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
  },
  subtleButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    background: "#ffffff",
    color: "#333333",
    padding: "8px 14px",
    fontSize: "12px",
    fontWeight: 700,
  },
  modalPrimaryButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "none",
    background: "#000000",
    color: "#ffffff",
    padding: "8px 15px",
    fontSize: "12px",
    fontWeight: 800,
    boxShadow: "0 12px 24px rgba(15, 23, 42, 0.22)",
  },
  modalPrimaryButtonDisabled: {
    opacity: 0.72,
    cursor: "not-allowed",
    boxShadow: "none",
  },
};

export default function TimeLogs() {
  const usrType = localStorage.getItem("userType");

  const [timeLogInvoices, setTimeLogInvoices] = useState([]);
  const [timeLogs, setTimeLogs] = useState([]);
  const [invType, setInvType] = useState("subcontractor");
  const [entityType, setEntityType] = useState("");
  const [projectStartDate, setProjectStartDate] = useState("");
  const [projectStDt, setProjectStDt] = useState("");
  const [currentStDt, setCurrentStDt] = useState("");
  const [notesType, setNotesType] = useState(
    usrType === "recruiter" ? "TimeLogRecruiter" : "TimeLog"
  );
  const [showHide, setShowHide] = useState(false);
  const [timeLogPopupMode, setTimeLogPopupMode] = useState("calendar");
  const [noteShowHide, setNoteShowHide] = useState(false);
  const [payShowHide, setPayShowHide] = useState(false);
  const [showHideTimeLog, setShowHideTimeLog] = useState(false);
  const [reportShowHide, setReportShowHide] = useState(false);
  const [reportPdfLoading, setReportPdfLoading] = useState(false);
  const [timeLogObj, setTimeLogObj] = useState(null);
  const [reportTimeLogObj, setReportTimeLogObj] = useState(null);
  const [candidateObj, setCandidateObj] = useState(null);
  const [appAllTasks, setAllAppTasks] = useState([]);
  const [allRates, setAllRates] = useState([]);

  const timeLogsSelector = useSelector(getTimeLogs);
  const timeLogInvoiceSelector = useSelector(getTimeLogInvoice);
  const timeLogDurationSelector = useSelector(getTimeLogDuration);
  const allRatesSelector = useSelector(getAllRates);
  const timeLogReportStatusSelector = useSelector(getTimeLogReportStatus);
  const updateStatusSelector = useSelector(getProjectDateUpdateStatus);
  const userDetailsSelector = useSelector(getLoggedUserDetails);
  const timeSheetDelStatusSelector = useSelector(getTimeSheetDelStatus);
  const candidateAppAllTasksSelector = useSelector(getCandidateAppAllTasks);

  const [handleDownloadFile] = useDocDownload();

  const currentUser = getCurrentUser();
  const userId = currentUser?.id;
  const dispatch = useDispatch();

  // RTK Query Hooks
  const { data: timeLogsQueryData = [], isLoading: isLoadingTimeLogs, isFetching: isFetchingTimeLogs, refetch: refetchTimeLogs } = useLoadTimeLogsQuery(userId, { skip: !userId || !entityType });
  const { data: timeLogInvoiceQueryData = [], isLoading: isLoadingInvoices, isFetching: isFetchingInvoices, refetch: refetchTimeLogInvoices } = useLoadTimeLogInvoiceQuery(userId, { skip: !userId || !entityType });
  const { data: timeLogDurationQueryData = null, isLoading: isLoadingDuration, isFetching: isFetchingDuration, refetch: refetchTimeLogDuration } = useLoadTimeLogDurationQuery(userId, { skip: !userId || !entityType });
  const { data: allRatesQueryData = [], isLoading: isLoadingRates, isFetching: isFetchingRates, refetch: refetchAllRates } = useLoadAllRatesQuery(userId, { skip: !userId || !entityType });

  const isInitialLoading = isLoadingTimeLogs || isLoadingInvoices || isLoadingDuration || isLoadingRates;
  const isDataFetching = isFetchingTimeLogs || isFetchingInvoices || isFetchingDuration || isFetchingRates;
  const isDataLoading = isInitialLoading || (isDataFetching && timeLogs.length === 0);

  const handleRefreshLogs = useCallback(() => {
    refetchTimeLogs();
    refetchTimeLogInvoices();
    refetchTimeLogDuration();
    refetchAllRates();
  }, [refetchTimeLogs, refetchTimeLogInvoices, refetchTimeLogDuration, refetchAllRates]);

  const [updateProjectStartDateMut] = useUpdateProjectStartDateMutation();
  const [deleteTimeSheetDocMut] = useDeleteTimeSheetDocMutation();
  const [setTimeLogReportStatusMut] = useSetTimeLogReportStatusMutation();

  useEffect(() => {
    if (userId && !userDetailsSelector) {
      dispatch(loadUserDetails(userId));
    }
  }, [dispatch, userId, userDetailsSelector]);

  useEffect(() => {
    if (userDetailsSelector) {
      const cnObj = userDetailsSelector;
      const entityType = getFieldData(
        cnObj.performerData,
        "candidateonboardingform",
        "CandidateEntity"
      );
      setCandidateObj(cnObj);
      setEntityType(entityType);
    }
  }, [userDetailsSelector]);

  useEffect(() => {
    setAllAppTasks(candidateAppAllTasksSelector);
  }, [candidateAppAllTasksSelector]);

  useEffect(() => {
    setAllRates(allRatesSelector);
  }, [allRatesSelector]);

  useEffect(() => {
    setTimeLogs(
      [...timeLogsSelector].sort((a, b) => {
        const yearDiff = Number(b.year || 0) - Number(a.year || 0);
        if (yearDiff !== 0) return yearDiff;
        return Number(b.month || 0) - Number(a.month || 0);
      })
    );
    if (timeLogInvoiceSelector.length > 0) {
      let invoType = invType;
      if (usrType === "candidate") invoType = usrType;
      if (usrType === "candidate" && entityType === "C2C Basis")
        invoType = "subcontractor";
      const filterInvoices = timeLogInvoiceSelector.filter(
        (x) => x.invoiceType === invoType
      );
      setTimeLogInvoices(filterInvoices);
    }
    if (timeLogDurationSelector?.startDate) {
      const startDate = new Date(timeLogDurationSelector.startDate);
      startDate.setDate(startDate.getDate()); // add 1 day
      const result = startDate.toISOString().substring(0, 10);
      setProjectStDt(timeLogDurationSelector.projectStartedDate || startDate);
      setCurrentStDt(timeLogDurationSelector.projectStartedDate);
      setProjectStartDate(timeLogDurationSelector.projectStartedDate || result);
    }
  }, [timeLogsSelector, timeLogInvoiceSelector, timeLogDurationSelector, invType, usrType, entityType]);

  useEffect(() => {
    if (updateStatusSelector) {
      getAlert("", "Saved Successfully", "success");
    }
  }, [updateStatusSelector]);

  useEffect(() => {
    if (timeLogReportStatusSelector && reportTimeLogObj?.id) {
      setTimeLogs((prevTimeLogs) =>
        prevTimeLogs.map((item) =>
          item.id === reportTimeLogObj.id ? { ...item, status: true } : item
        )
      );
      setReportTimeLogObj((prevReportTimeLogObj) =>
        prevReportTimeLogObj ? { ...prevReportTimeLogObj, status: true } : prevReportTimeLogObj
      );
    }
  }, [timeLogReportStatusSelector, reportTimeLogObj]);

  const getInvoiceStatus = (item) => {
    const tmLogs = timeLogInvoices.filter(
      (x) => x.timeLogId === item.id && x.status !== null
    );
    if (tmLogs.length === 0) {
      return (
        <span style={{ ...TIME_LOG_STYLES.statusPill, ...TIME_LOG_STYLES.statusPending }}>
          <i className="fa fa-clock-o" aria-hidden="true"></i>
          Review Pending
        </span>
      );
    }

    return (
      <>
        {tmLogs.map((inv, index) => (
          <div key={index} style={TIME_LOG_STYLES.statusStack}>
            <span style={{ ...TIME_LOG_STYLES.statusPill, ...TIME_LOG_STYLES.statusReviewed }}>
              <i className="fa fa-eye" aria-hidden="true"></i>
              Reviewed
            </span>
            {inv.status === true && (
              <span style={{ ...TIME_LOG_STYLES.statusPill, ...TIME_LOG_STYLES.statusApproved }}>
                <i className="fa fa-check" aria-hidden="true"></i>
                Approved
              </span>
            )}
            {inv.status === false && (
              <span style={{ ...TIME_LOG_STYLES.statusPill, ...TIME_LOG_STYLES.statusRejected }}>
                <i className="fa fa-times" aria-hidden="true"></i>
                Rejected
              </span>
            )}
            <span style={TIME_LOG_STYLES.statusDate}>
              <Moment format="MMM Do YYYY">{inv.reviewDateTime}</Moment>
            </span>
          </div>
        ))}
      </>
    );
  };

  const getPaymentStatus = (item) => {
    const tmLogs = timeLogInvoices.filter((x) => x.timeLogId === item.id);

    if (tmLogs.length === 0) {
      return <span style={TIME_LOG_STYLES.emptyIndicator}>—</span>;
    }

    return (
      <>
        {tmLogs.map((pay, index) => (
          <div key={index} style={TIME_LOG_STYLES.paymentBlock}>
            {pay.paymentMode ? (
              <>
                {pay.docList?.length > 0 && (
                  <div style={TIME_LOG_STYLES.paymentDocList}>
                    {pay.docList.map((doc) => (
                      <button
                        key={doc.id || doc.documentName}
                        type="button"
                        className="btn btn-sm"
                        style={TIME_LOG_STYLES.paymentDocButton}
                        title={`Download ${doc.documentName}`}
                        onClick={() => handleDownloadFile(doc)}
                      >
                        <i
                          className={`fa ${getDocumentIcon(doc)}`}
                          aria-hidden="true"
                          style={getDocumentIconStyle(doc)}
                        ></i>
                        <span style={TIME_LOG_STYLES.paymentDocName}>
                          {doc.documentName}
                        </span>
                        <i
                          className="fa fa-download"
                          style={TIME_LOG_STYLES.paymentDocDownloadIcon}
                          aria-hidden="true"
                        ></i>
                      </button>
                    ))}
                  </div>
                )}

                {(pay.paymentDate || pay.addedDatetime) && (
                  <div style={TIME_LOG_STYLES.paymentDateWrap}>
                    <Moment format="MMM Do YYYY">
                      {pay.paymentDate || pay.addedDatetime}
                    </Moment>
                    {pay.paymentDate && (
                      <>
                        {" "}
                        <Moment format="h:mm a">{pay.paymentDate}</Moment>
                      </>
                    )}
                  </div>
                )}

                <div style={TIME_LOG_STYLES.paymentMetricsList}>
                  {entityType === "W2 Basis" && (
                    <div style={TIME_LOG_STYLES.paymentMetricRow}>
                      <span style={TIME_LOG_STYLES.paymentMetricLabel}>
                        Deduction:
                      </span>
                      <span style={TIME_LOG_STYLES.paymentMetricVal}>
                        {pay?.adpDeduction ? (
                          <RateEncryptDecrypt
                            rateData={pay.adpDeduction}
                            endSymbol=""
                          />
                        ) : (
                          <span style={{ color: "#94a3b8" }}>$0.00</span>
                        )}
                      </span>
                    </div>
                  )}
                  <div style={TIME_LOG_STYLES.paymentMetricRow}>
                    <span style={TIME_LOG_STYLES.paymentMetricLabel}>
                      Paid:
                    </span>
                    <span style={TIME_LOG_STYLES.paymentMetricVal}>
                      {pay?.paidAmount ? (
                        <RateEncryptDecrypt
                          rateData={pay.paidAmount}
                          endSymbol=""
                        />
                      ) : (
                        <span style={{ color: "#94a3b8" }}>$0.00</span>
                      )}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <span style={TIME_LOG_STYLES.emptyIndicator}>—</span>
            )}
          </div>
        ))}
      </>
    );
  };

  const getPerDiemStatus = (item) => {
    const prd = getFieldData(
      candidateObj?.performerData,
      "candidateonboardingform",
      "Per_Diem_Rate"
    );

    if (!prd) {
      return (
        <span style={TIME_LOG_STYLES.emptyIndicator}>—</span>
      );
    }

    const totalDays = getTotalDays(item?.workingHrs);
    const totalPerDiem = Number(prd) * totalDays;

    return (
      <div style={TIME_LOG_STYLES.perDiemStack}>
        <div style={TIME_LOG_STYLES.perDiemRow}>
          <span style={TIME_LOG_STYLES.perDiemLabel}>Rate:</span>
          <span style={TIME_LOG_STYLES.perDiemValue}>
            <RateEncryptDecrypt rateData={prd} endSymbol="/day" />
          </span>
        </div>
        <div style={TIME_LOG_STYLES.perDiemRow}>
          <span style={TIME_LOG_STYLES.perDiemLabel}>Total:</span>
          <span style={TIME_LOG_STYLES.perDiemValue}>
            <RateEncryptDecrypt
              rateData={totalPerDiem}
              endSymbol=""
            />
          </span>
        </div>
      </div>
    );
  };

  const getTimeLogStatus = (item) => {
    if (canUploadTimesheet(item)) {
      return (
        <div style={TIME_LOG_STYLES.actionStack}>
          <button
            type="button"
            className="btn btn-sm"
            style={TIME_LOG_STYLES.actionLink}
            onClick={() => handleShowHide(item, "calendar")}
          >
            <i className="fa fa-pencil" aria-hidden="true" style={{ color: "#000000" }}></i>
            Edit
          </button>
        </div>
      );
    }

    // Approved & paid → no action
    return null;
  };


  const getMonthlyPeriod = (obj) => {
    if (!obj?.workingHrs) return null;

    let wt;
    try {
      wt = JSON.parse(obj.workingHrs);
    } catch (e) {
      return null;
    }

    if (!Array.isArray(wt) || wt.length === 0) return null;

    const sortWt = [...wt].sort(
      (a, b) => new Date(a.start) - new Date(b.start)
    );

    const first = sortWt[0];
    const last = sortWt[sortWt.length - 1];

    const lastDate =
      last.type === "m"
        ? getMonthLastDay(last.start)
        : last.start;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "5px", textAlign: "left" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 600, color: "#000000" }}>
          <span style={{ color: "#94a3b8", fontWeight: 600, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.04em" }}>FROM</span>
          <Moment format="MM/DD/YYYY">{first.start}</Moment>
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 600, color: "#000000" }}>
          <span style={{ color: "#94a3b8", fontWeight: 600, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.04em" }}>TO</span>
          <Moment format="MM/DD/YYYY">{lastDate}</Moment>
        </span>
      </div>
    );
  };

  const handleNotesComments = (type, obj) => {
    if (type === "save") {
      setTimeLogObj(null);
      setNoteShowHide(false);
    } else {
      setTimeLogObj(obj);
      setNoteShowHide(!noteShowHide);
    }
  };

  const handlePayShowHide = (obj) => {
    setPayShowHide(!payShowHide);
    setTimeLogObj(obj);
  };

  const handleReportTimeLog = (obj) => {
    if (!obj) {
      setReportTimeLogObj(null);
      setReportShowHide(false);
      setReportPdfLoading(false);
      return;
    }

    setReportTimeLogObj(obj);
    setReportShowHide(true);
  };

  const getCandidateName = () =>
    [candidateObj?.performername, candidateObj?.lastName].filter(Boolean).join(" ") ||
    currentUser?.name ||
    "N/A";

  console.log(candidateObj)
  const getCandidateId = () =>
    candidateObj?.performerid || currentUser?.id || "N/A";

  const getIpcsId = () =>
    candidateObj?.maxloans || "N/A";

  const formatCurrency = (value) => `$${formatAmount(Number(value || 0).toFixed(2))}`;

  const isTimeLogReportGenerated = (item) =>
    item?.status === true || item?.status === "true" || item?.status === 1;

  const canUploadTimesheet = (item) => {
    const invoices = timeLogInvoices.filter((x) => x.timeLogId === item.id);
    const hasPaidAmount = invoices.some((x) => x.paidAmount > 0);
    const hasApprovedInvoice = invoices.some((x) => x.status === true);

    return !hasPaidAmount || !hasApprovedInvoice;
  };

  const getCandidateRate = (item) => {
    const itemYear = parseInt(item?.year, 10);
    const itemMonth = parseInt(item?.month, 10);

    const matchedRate = Array.isArray(allRates)
      ? allRates.reduce((best, rate, index) => {
        if (!itemYear || !itemMonth) return best;

        const effMs = new Date(rate?.candidateEffectiveDate).getTime();
        if (!Number.isFinite(effMs)) return best;

        const eff = new Date(effMs);
        const effYear = eff.getFullYear();
        const effMonth = eff.getMonth() + 1;
        const isApplicable =
          effYear < itemYear || (effYear === itemYear && effMonth <= itemMonth);

        if (!isApplicable) return best;

        if (!best) return { ...rate, index };

        const bestMs = new Date(best?.candidateEffectiveDate).getTime();
        if (effMs > bestMs) return { ...rate, index };
        if (effMs === bestMs && index > best.index) return { ...rate, index };

        return best;
      }, null)
      : null;

    const candidateRate =
      matchedRate?.candidateRate !== null &&
        matchedRate?.candidateRate !== undefined &&
        matchedRate?.candidateRate !== ""
        ? matchedRate.candidateRate
        : candidateObj?.rates?.candidateRate;

    return Number(candidateRate) || 0;
  };

  const getTimeLogReportDetails = (item) => {
    if (!item) {
      return {
        candidateName: "N/A",
        candidateId: "N/A",
        ipcsId: "N/A",
        monthYear: "",
        hours: 0,
        hoursLabel: "0h 0m",
        days: 0,
        rate: 0,
        totalCompensation: 0,
        positionTitle: "N/A",
        agencyName: "N/A",
      };
    }

    const hours = Number(getTotalHours(item?.workingHrs) || 0);
    const rate = getCandidateRate(item);
    const monthYear = `${getMonthName(item.month)} ${item.year}`;

    return {
      candidateName: getCandidateName(),
      candidateId: getCandidateId(),
      ipcsId: getIpcsId(),
      monthYear,
      hours,
      hoursLabel: formatTotalHours(item?.workingHrs),
      days: getTotalDays(item?.workingHrs),
      rate,
      totalCompensation: hours * rate,
      positionTitle:
        getFieldData(candidateObj?.performerData, "projectengagement", "PositionTitle") ||
        "N/A",
      agencyName:
        getFieldData(candidateObj?.performerData, "projectengagement", "AgencyName") ||
        "N/A",
    };
  };

  const generateTimeLogReportPdf = () => {
    if (!reportTimeLogObj || reportPdfLoading) return;

    setReportPdfLoading(true);
    const previewWindow = window.open("", "_blank");

    if (previewWindow) {
      previewWindow.document.write(
        "<html><head><title>Preparing PDF</title></head><body style='font-family:Arial,sans-serif;padding:24px;color:#333333;'>Preparing PDF preview...</body></html>"
      );
      previewWindow.document.close();
    }

    setTimeout(() => {
      try {
        const report = getTimeLogReportDetails(reportTimeLogObj);
        const doc = new jsPDF("portrait", "pt", "a4");
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 42;
        const contentWidth = pageWidth - margin * 2;

        doc.setFillColor(241, 245, 249);
        doc.setDrawColor(226, 232, 240);
        doc.roundedRect(margin, 40, contentWidth, 108, 14, 14, "FD");
        doc.setFillColor(15, 23, 42);
        doc.roundedRect(margin + 10, 54, 2, 80, 1, 1, "F");

        doc.setTextColor(15, 23, 42);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(21);
        doc.text("Report Time Log", margin + 24, 78);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10.5);
        doc.setTextColor(71, 85, 105);
        doc.text("Generated monthly report preview", margin + 24, 102);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text(`Generated: ${new Date().toLocaleDateString("en-US")}`, margin + 24, 126);

        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(219, 234, 254);
        const statusPillX = pageWidth - margin - 118;
        const statusPillY = 64;
        const statusPillW = 88;
        const statusPillH = 34;
        const statusText = "GENERATED";
        doc.roundedRect(statusPillX, statusPillY, statusPillW, statusPillH, 17, 17, "FD");
        doc.setTextColor(15, 23, 42);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text(
          statusText,
          statusPillX + (statusPillW - doc.getTextWidth(statusText)) / 2,
          statusPillY + 21
        );

        const detailCards = [
          ["Candidate", report.candidateName],
          ["System ID", report.candidateId.toString()],
          ["IPCS ID", report.ipcsId.toString()],
          ["Position", report.positionTitle],
          ["Agency", report.agencyName],
        ];

        const detailCardW = (contentWidth - 14) / 2;
        const detailCardH = 62;
        const detailStartY = 174;
        detailCards.forEach(([label, value], index) => {
          const isLastAndOdd = index === detailCards.length - 1 && detailCards.length % 2 !== 0;
          const cardX = isLastAndOdd ? margin : margin + (index % 2) * (detailCardW + 14);
          const cardY = detailStartY + Math.floor(index / 2) * (detailCardH + 14);
          const cardW = isLastAndOdd ? contentWidth : detailCardW;

          doc.setFillColor(248, 250, 252);
          doc.setDrawColor(226, 232, 240);
          doc.roundedRect(cardX, cardY, cardW, detailCardH, 10, 10, "FD");
          doc.setTextColor(100, 116, 139);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.text(label.toUpperCase(), cardX + 16, cardY + 21);
          doc.setTextColor(15, 23, 42);
          doc.setFontSize(12);
          doc.text(doc.splitTextToSize(value || "N/A", cardW - 32), cardX + 16, cardY + 43);
        });

        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(226, 232, 240);
        doc.roundedRect(margin, 418, contentWidth, 244, 14, 14, "FD");

        doc.setTextColor(15, 23, 42);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text(`${entityType === "1099 Basis" ? "1099" : "W2"} - Time Log Reporting`, margin + 20, 450);

        doc.setTextColor(100, 116, 139);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text(`Selected month time log report for ${report.monthYear}.`, margin + 20, 470);

        const reportCards = [
          ["Month / Year", report.monthYear, [15, 23, 42], "Calendar period"],
          ["Approved Hours", report.hoursLabel, [14, 116, 144], "Logged and approved by client"],
          ["Rate / Hour", formatCurrency(report.rate), [124, 58, 237], "Applicable hourly rate"],
          ["Total Compensation", formatCurrency(report.totalCompensation), [22, 101, 52], "Less applicable Taxes"],
        ];

        const reportCardW = (contentWidth - 54) / 2;
        const reportCardH = 68;
        const reportStartY = 496;
        reportCards.forEach(([label, value, color, hint], index) => {
          const reportCardX = margin + 20 + (index % 2) * (reportCardW + 14);
          const reportCardY = reportStartY + Math.floor(index / 2) * (reportCardH + 16);
          doc.setFillColor(248, 250, 252);
          doc.setDrawColor(226, 232, 240);
          doc.roundedRect(reportCardX, reportCardY, reportCardW, reportCardH, 10, 10, "FD");
          doc.setFillColor(color[0], color[1], color[2]);
          doc.roundedRect(reportCardX + 8, reportCardY + 10, 2, reportCardH - 20, 1, 1, "F");
          doc.setTextColor(100, 116, 139);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.text(label.toUpperCase(), reportCardX + 17, reportCardY + 20);
          doc.setTextColor(15, 23, 42);
          doc.setFontSize(16);
          doc.text(value, reportCardX + 17, reportCardY + 43);
          doc.setTextColor(100, 116, 139);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.text(hint, reportCardX + 17, reportCardY + 58);
        });

        doc.setDrawColor(226, 232, 240);
        doc.line(margin, pageHeight - 76, pageWidth - margin, pageHeight - 76);
        doc.setTextColor(100, 116, 139);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text("This report is generated from submitted monthly time log data.", margin, pageHeight - 52);
        doc.text("IPCS Customer Portal", pageWidth - margin - 102, pageHeight - 52);

        const blobUrl = doc.output("bloburl");
        if (previewWindow) {
          previewWindow.location.href = blobUrl;
        } else {
          window.open(blobUrl, "_blank");
        }

        if (!isTimeLogReportGenerated(reportTimeLogObj)) {
          setTimeLogReportStatusMut({ id: reportTimeLogObj.id, updatedBy: userId });
        }
      } finally {
        setReportPdfLoading(false);
      }
    }, 150);
  };

  const handleSaveProjectStartDate = () => {
    updateProjectStartDateMut({ id: userId, date: projectStartDate });
  };

  const handleStartDate = ({ currentTarget }) => {
    setProjectStartDate(currentTarget.value);
  };

  const handleShowHide = useCallback((obj, mode = "calendar") => {
    if (obj === "save") {
      setTimeLogObj("");
      setShowHide(false);
      setTimeLogPopupMode("calendar");
      refetchTimeLogs();
    } else {
      setShowHide(obj === null ? false : true);
      setTimeLogObj(obj);
      setTimeLogPopupMode(mode);
    }
  }, [refetchTimeLogs]);

  const handleDeleteTimeSheetDoc = useCallback(async (id) => {
    const result = await getConfirmAlert(
      "Warning",
      "Are you sure you want to delete this Time Sheet?",
      "warning"
    );
    if (result.isConfirmed) {
      deleteTimeSheetDocMut(id);
    }
  }, [deleteTimeSheetDocMut]);

  useEffect(() => {
    if (timeSheetDelStatusSelector) {
      getAlert("", "Deleted Successfully", "success");
    }
  }, [timeSheetDelStatusSelector]);

  let startDt = projectStartDate;
  if (startDt === "") startDt = new Date();
  let pgTitle = "Time Log Management";
  if (entityType !== "C2C Basis") pgTitle = "Time Log and Payments";
  const totalLoggedHours = timeLogs.reduce(
    (sum, log) => sum + Number(getTotalHours(log?.workingHrs) || 0),
    0
  );
  const formatNumericHours = (hours) => {
    const totalMinutes = Math.round((Number(hours) || 0) * 60);
    const finalHours = Math.floor(totalMinutes / 60);
    const finalMinutes = totalMinutes % 60;

    return `${finalHours}h ${finalMinutes}m`;
  };
  const totalLoggedDays = timeLogs.reduce(
    (sum, log) => sum + Number(getTotalDays(log?.workingHrs) || 0),
    0
  );
  const reviewedLogs = timeLogs.filter((item) =>
    timeLogInvoices.some((x) => x.timeLogId === item.id && x.status !== null)
  ).length;
  const paidLogs = timeLogs.filter((item) =>
    timeLogInvoices.some((x) => x.timeLogId === item.id && x.paidAmount > 0)
  ).length;
  const showReportTimeLogColumn =
    entityType === "W2 Basis" || entityType === "1099 Basis";

  return (
    <React.Fragment>
      <div className="form-group mt-3">
        <div style={TIME_LOG_STYLES.card}>
          <div style={TIME_LOG_STYLES.cardHeader}>
            <div style={TIME_LOG_STYLES.titleRow}>
              <div style={TIME_LOG_STYLES.titleWrap}>
                <span style={TIME_LOG_STYLES.titleIcon}>
                  <i className="fa fa-clock-o" aria-hidden="true"></i>
                </span>
                <div>
                  <h1 style={TIME_LOG_STYLES.title}>{pgTitle}</h1>
                  <p style={TIME_LOG_STYLES.subtitle}>
                    Track submitted time, supporting timesheets, review status, and payments in one place.
                  </p>
                </div>
              </div>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={TIME_LOG_STYLES.refreshButton}
                  onClick={handleRefreshLogs}
                  disabled={isDataFetching}
                >
                  <i className="fa fa-refresh" aria-hidden="true"></i> Refresh
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={TIME_LOG_STYLES.primaryButton}
                  disabled={
                    appAllTasks.filter((x) => x.loanPhase_Desc === "Time Log")
                      .length === 0
                  }
                  onClick={() => handleShowHide("", "calendar")}
                >
                  <i className="fa fa-plus" aria-hidden="true"></i> Add Time Log
                </button>
              </div>
            </div>
          </div>
          <div style={TIME_LOG_STYLES.cardBody}>
            {isDataFetching && timeLogs.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 18px', background: '#eff6ff', borderTop: '1px solid #bfdbfe', borderBottom: '1px solid #bfdbfe', color: '#1e40af', fontSize: '12px', fontWeight: 600 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  <i className='fa fa-circle-o-notch fa-spin' style={{ color: '#2563eb', fontSize: '13px' }}></i>
                  <span>Fetching updated time logs...</span>
                </div>
                <span style={{ fontSize: '11px', color: '#3b82f6', fontWeight: 500 }}>Updating</span>
              </div>
            )}
            <div style={{ opacity: isDataFetching && timeLogs.length > 0 ? 0.7 : 1, transition: 'opacity 0.2s ease' }}>
            <div style={TIME_LOG_STYLES.statsRow}>
              <div style={TIME_LOG_STYLES.statCard}>
                <div style={TIME_LOG_STYLES.statIcon}>
                  <i className="fa fa-calendar-o" aria-hidden="true"></i>
                </div>
                <div style={TIME_LOG_STYLES.statContent}>
                  <div style={TIME_LOG_STYLES.statLabel}>Time Logs</div>
                  <div style={TIME_LOG_STYLES.statValue}>{timeLogs.length}</div>
                </div>
              </div>
              <div style={TIME_LOG_STYLES.statCard}>
                <div style={TIME_LOG_STYLES.statIcon}>
                  <i className="fa fa-clock-o" aria-hidden="true"></i>
                </div>
                <div style={TIME_LOG_STYLES.statContent}>
                  <div style={TIME_LOG_STYLES.statLabel}>Total Hours</div>
                  <div style={TIME_LOG_STYLES.statValue}>{formatNumericHours(totalLoggedHours)}</div>
                </div>
              </div>
              <div style={TIME_LOG_STYLES.statCard}>
                <div style={TIME_LOG_STYLES.statIcon}>
                  <i className="fa fa-briefcase" aria-hidden="true"></i>
                </div>
                <div style={TIME_LOG_STYLES.statContent}>
                  <div style={TIME_LOG_STYLES.statLabel}>Total Days</div>
                  <div style={TIME_LOG_STYLES.statValue}>{totalLoggedDays}</div>
                </div>
              </div>
              <div style={TIME_LOG_STYLES.statCard}>
                <div style={TIME_LOG_STYLES.statIcon}>
                  <i className="fa fa-check-circle-o" aria-hidden="true"></i>
                </div>
                <div style={TIME_LOG_STYLES.statContent}>
                  <div style={TIME_LOG_STYLES.statLabel}>Reviewed / Paid</div>
                  <div style={TIME_LOG_STYLES.statValue}>{reviewedLogs} / {paidLogs}</div>
                </div>
              </div>
            </div>
            <div style={TIME_LOG_STYLES.utilityRow}>
              <div style={TIME_LOG_STYLES.utilityPanel}>
                <div style={TIME_LOG_STYLES.utilityPanelTop}>
                  <span style={TIME_LOG_STYLES.utilityIcon}>
                    <i className="fa fa-calendar" aria-hidden="true"></i>
                  </span>
                  <div style={TIME_LOG_STYLES.utilityCopy}>
                    <div style={TIME_LOG_STYLES.utilityLabel}>Date Started On The Project</div>
                    <div style={TIME_LOG_STYLES.utilityValue}>
                      {projectStDt !== ""
                        ? new Date(projectStDt).toISOString().substring(0, 10)
                        : new Date(startDt).toISOString().substring(0, 10)}
                    </div>
                  </div>
                </div>
                {projectStDt !== "" ? (
                  <div style={TIME_LOG_STYLES.utilityActions}>
                    <button
                      type="button"
                      className="btn btn-sm"
                      style={TIME_LOG_STYLES.utilityButton}
                      disabled={
                        appAllTasks.filter((x) => x.loanPhase_Desc === "Time Log")
                          .length === 0
                      }
                      onClick={() => setProjectStDt("")}
                    >
                      <i className="fa fa-edit" aria-hidden="true" style={{ color: "#666666" }}></i> Edit
                    </button>
                  </div>
                ) : (
                  <div style={TIME_LOG_STYLES.utilityEditor}>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={TIME_LOG_STYLES.utilityInput}
                      value={new Date(startDt).toISOString().substring(0, 10)}
                      onChange={handleStartDate}
                    />
                    <button
                      type="button"
                      className="btn btn-sm mr-0"
                      style={TIME_LOG_STYLES.utilityButton}
                      onClick={handleSaveProjectStartDate}
                    >
                      <i className="fa fa-check" aria-hidden="true" style={{ color: "#666666" }}></i> Save
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      style={TIME_LOG_STYLES.utilityButton}
                      onClick={() => setProjectStDt(currentStDt)}
                    >
                      <i className="fa fa-times" aria-hidden="true" style={{ color: "#666666" }}></i> Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div style={TIME_LOG_STYLES.tableWrap}>
              <div className="table-responsive">
                <table className="table align-items-center table-hover mb-0" style={TIME_LOG_STYLES.table}>
                <thead>
                  <tr>
                    <th style={TIME_LOG_STYLES.tableHeadCell}>
                      Month
                      <span style={TIME_LOG_STYLES.tableHeadSubtext}>Year</span>
                    </th>
                    <th style={TIME_LOG_STYLES.tableHeadCell}>
                      Period
                    </th>
                    <th style={TIME_LOG_STYLES.tableHeadCell}>
                      Hours
                      <span style={TIME_LOG_STYLES.tableHeadSubtext}>Days</span>
                    </th>
                    <th style={TIME_LOG_STYLES.tableHeadCell}>
                      Approved
                      <span style={TIME_LOG_STYLES.tableHeadSubtext}>Timesheets</span>
                    </th>

                    {entityType !== "W2 Basis" && <th style={TIME_LOG_STYLES.tableHeadCell}>Status</th>}

                    <th style={TIME_LOG_STYLES.tableHeadCell}>
                      Review Status
                    </th>

                    {(entityType === "W2 Basis" || entityType !== "C2C Basis") && (
                      <th style={TIME_LOG_STYLES.tableHeadCell}>
                        Payment
                      </th>
                    )}

                    {entityType === "W2 Basis" && (
                      <th style={TIME_LOG_STYLES.tableHeadCell}>
                        Per Diem Rate
                        <span style={TIME_LOG_STYLES.tableHeadSubtext}>Amount ($)</span>
                      </th>
                    )}

                    <th style={TIME_LOG_STYLES.tableHeadCell}>Action</th>
                    {showReportTimeLogColumn && (
                      <th style={TIME_LOG_STYLES.tableHeadCell}>Report Time Log</th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {isDataLoading ? (
                    <tr>
                      <td colSpan="10" style={{ padding: "44px 20px", textAlign: "center", background: "#ffffff" }}>
                        <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                          <i className="fa fa-circle-o-notch fa-spin" style={{ fontSize: "26px", color: "#000000" }}></i>
                          <div style={{ fontSize: "14px", fontWeight: 600, color: "#000000" }}>Loading time logs...</div>
                          <div style={{ fontSize: "12px", fontWeight: 600, color: "#666666" }}>Please wait while we retrieve submitted time records.</div>
                        </div>
                      </td>
                    </tr>
                  ) : timeLogs.length === 0 ? (
                    <tr>
                      <td colSpan="10" style={{ padding: "48px 20px", textAlign: "center", background: "#ffffff" }}>
                        <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "12px", maxWidth: "380px", margin: "0 auto" }}>
                          <div style={{ width: "48px", height: "48px", borderRadius: "0px", background: "#f5f5f5", color: "#000000", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", border: "1px solid #e2e8f0" }}>
                            <i className="fa fa-calendar-minus-o" aria-hidden="true"></i>
                          </div>
                          <div>
                            <div style={{ fontSize: "15px", fontWeight: 600, color: "#000000", marginBottom: "4px" }}>No Time Logs Found</div>
                            <div style={{ fontSize: "13px", fontWeight: 600, color: "#666666", lineHeight: 1.5 }}>There are no submitted time log periods available for this candidate. Click "Add Time Log" to record hours.</div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    timeLogs.map((item) => (
                      <tr key={item.id}>
                        <td style={TIME_LOG_STYLES.cell}>
                          <span style={TIME_LOG_STYLES.monthYearStack}>
                            <span style={TIME_LOG_STYLES.monthYearPrimary}>
                              {getMonthName(item.month)}
                            </span>
                            <span style={TIME_LOG_STYLES.monthYearSecondary}>
                              {item.year}
                            </span>
                          </span>
                        </td>

                        <td style={TIME_LOG_STYLES.cell}>
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={TIME_LOG_STYLES.periodText}
                            onClick={() => handleShowHide(item, "calendar")}
                          >
                            {getMonthlyPeriod(item)}
                          </button>
                        </td>

                        <td style={TIME_LOG_STYLES.cell}>
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={TIME_LOG_STYLES.metricButton}
                            onClick={() => handleShowHide(item, "calendar")}
                          >
                            <span style={TIME_LOG_STYLES.metricPrimary}>
                              {formatTotalHours(item?.workingHrs)}
                            </span>
                            <span style={TIME_LOG_STYLES.metricSecondary}>
                              {`${getTotalDays(item.workingHrs)} ${getTotalDays(item.workingHrs) === 1 || getTotalDays(item.workingHrs) === 0 ? "DAY" : "DAYS"}`}
                            </span>
                          </button>
                        </td>

                        {/* Approved Timesheets */}
                        <td style={TIME_LOG_STYLES.cell}>
                          <ul style={TIME_LOG_STYLES.docList}>
                            {item.docList?.map((doc) => {
                              const isLocked = timeLogInvoices.some(
                                (x) => x.timeLogId === item.id && x.status === true
                              );

                              return (
                                <li key={doc.id} style={TIME_LOG_STYLES.docRow}>
                                  <button
                                    type="button"
                                    style={TIME_LOG_STYLES.docLink}
                                    onClick={() => handleDownloadFile(doc)}
                                  >
                                    {doc.documentName}
                                  </button>

                                  <button
                                    type="button"
                                    style={TIME_LOG_STYLES.iconButton}
                                    title="Delete Timesheet"
                                    disabled={isLocked}
                                    onClick={() => handleDeleteTimeSheetDoc(doc.id)}
                                  >
                                    <i className="fa fa-trash text-danger" />
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                          {canUploadTimesheet(item) && (
                            <button
                              type="button"
                              className="btn btn-sm"
                              style={{
                                ...TIME_LOG_STYLES.docUploadButton,
                                marginTop: item.docList?.length ? "10px" : 0,
                              }}
                              onClick={() => handleShowHide(item, "documents")}
                            >
                              <i className="fa fa-upload" aria-hidden="true" style={{ color: "#000000" }}></i>
                              Upload
                            </button>
                          )}
                        </td>

                        {entityType !== "W2 Basis" && (
                          <td style={TIME_LOG_STYLES.cell}>{getInvoiceStatus(item)}</td>
                        )}

                        {/* Notes */}
                        <td style={TIME_LOG_STYLES.cell}>
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={TIME_LOG_STYLES.notesButton}
                            onClick={() => handleNotesComments("", item)}
                          ><i className="fa fa-commenting-o" aria-hidden="true" style={{ fontSize: "12px", margin: 0, padding: 0 }}></i><span>View</span><span style={TIME_LOG_STYLES.notesBadge}>{item?.allComments?.filter((x) => x.commentType === notesType).length}</span></button>
                        </td>

                        {entityType !== "C2C Basis" && (
                          <td style={TIME_LOG_STYLES.cell}>{getPaymentStatus(item)}</td>
                        )}

                        {entityType === "W2 Basis" && (
                          <td style={TIME_LOG_STYLES.cell}>{getPerDiemStatus(item)}</td>
                        )}

                        <td style={TIME_LOG_STYLES.cell}>{getTimeLogStatus(item)}</td>
                        {showReportTimeLogColumn && (
                          <td style={TIME_LOG_STYLES.cell}>
                            <button
                              type="button"
                              className="btn btn-sm"
                              style={{
                                ...TIME_LOG_STYLES.reportButton,
                                ...(isTimeLogReportGenerated(item)
                                  ? TIME_LOG_STYLES.reportGeneratedButton
                                  : {}),
                              }}
                              onClick={() => handleReportTimeLog(item)}
                            >
                              <i
                                className={
                                  isTimeLogReportGenerated(item)
                                    ? "fa fa-check-circle"
                                    : "fa fa-cog"
                                }
                                aria-hidden="true"
                                style={{ color: isTimeLogReportGenerated(item) ? "#166534" : "#000000" }}
                              ></i>
                              {isTimeLogReportGenerated(item) ? "Generated" : "Generate"}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              </div>
            </div>
            </div>
          </div>
        </div>
      </div>

      <AddNewTimeLog
        showHide={showHide}
        handleShowHide={handleShowHide}
        selectedObj={timeLogObj}
        popupMode={timeLogPopupMode}
        timeLogInvoices={timeLogInvoices}
        allTimeLogs={timeLogsSelector}
        candidateObj={candidateObj}
        projectStartDate={projectStDt}
      ></AddNewTimeLog>

      <AddEditNotes
        noteShowHide={noteShowHide}
        handleNoteShowHide={handleNotesComments}
        notesType={notesType}
        candidateObj={candidateObj}
        timeLogObj={timeLogObj}
      ></AddEditNotes>

      <CandidatePay
        payShowHide={payShowHide}
        handlePayShowHide={handlePayShowHide}
        selectedObj={timeLogObj}
        candidateObj={candidateObj}
        timeLogsData={timeLogsSelector}
      ></CandidatePay>

      <Modal
        show={reportShowHide}
        size="lg"
        centered
        onHide={() => handleReportTimeLog(null)}
      >
        <Modal.Header style={TIME_LOG_STYLES.modalHeader}>
          <Modal.Title as="div" style={TIME_LOG_STYLES.modalTitleWrap}>
            <span style={TIME_LOG_STYLES.modalIcon}>
              <i className="fa fa-file-text-o" aria-hidden="true"></i>
            </span>
            <div>
              <h2 style={TIME_LOG_STYLES.modalTitle}>Report Time Log</h2>
              <div style={TIME_LOG_STYLES.modalSubtitle}>
                {reportTimeLogObj
                  ? `${getMonthName(reportTimeLogObj.month)} ${reportTimeLogObj.year}`
                  : ""}
              </div>
            </div>
            <button
              type="button"
              style={TIME_LOG_STYLES.modalCloseButton}
              onClick={() => handleReportTimeLog(null)}
              aria-label="Close report time log popup"
            >
              <i className="fa fa-times" aria-hidden="true"></i>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={TIME_LOG_STYLES.modalBody}>
          {reportTimeLogObj && (
            <>
              {(() => {
                const report = getTimeLogReportDetails(reportTimeLogObj);

                return (
                  <>
                    <div style={TIME_LOG_STYLES.reportSummaryGrid}>
                      <div style={TIME_LOG_STYLES.reportSummaryCard}>
                        <div style={TIME_LOG_STYLES.reportSummaryLabel}>Candidate</div>
                        <div style={TIME_LOG_STYLES.reportSummaryValue}>{report.candidateName}</div>
                      </div>
                      <div style={TIME_LOG_STYLES.reportSummaryCard}>
                        <div style={TIME_LOG_STYLES.reportSummaryLabel}>System ID</div>
                        <div style={TIME_LOG_STYLES.reportSummaryValue}>{report.candidateId}</div>
                      </div>
                      <div style={TIME_LOG_STYLES.reportSummaryCard}>
                        <div style={TIME_LOG_STYLES.reportSummaryLabel}>IPCS ID</div>
                        <div style={TIME_LOG_STYLES.reportSummaryValue}>{report.ipcsId}</div>
                      </div>
                      <div style={TIME_LOG_STYLES.reportSummaryCard}>
                        <div style={TIME_LOG_STYLES.reportSummaryLabel}>Position</div>
                        <div style={TIME_LOG_STYLES.reportSummaryValue}>{report.positionTitle}</div>
                      </div>
                      <div style={TIME_LOG_STYLES.reportSummaryCard}>
                        <div style={TIME_LOG_STYLES.reportSummaryLabel}>Agency</div>
                        <div style={TIME_LOG_STYLES.reportSummaryValue}>{report.agencyName}</div>
                      </div>
                    </div>

                    <div style={TIME_LOG_STYLES.reportPreviewCard}>
                      <div style={TIME_LOG_STYLES.reportPreviewHeader}>
                        <h3 style={TIME_LOG_STYLES.reportPreviewTitle}>
                          {entityType === "1099 Basis" ? "1099" : "W2"} - Time Log Reporting
                        </h3>
                        <p style={TIME_LOG_STYLES.reportPreviewSubtitle}>
                          Selected month time log report for {report.monthYear}.
                        </p>
                      </div>
                      <div style={TIME_LOG_STYLES.reportMetricGrid}>
                        <div style={TIME_LOG_STYLES.reportMetricCard}>
                          <span
                            style={{
                              ...TIME_LOG_STYLES.reportMetricAccent,
                              background: "#000000",
                            }}
                          />
                          <span
                            style={{
                              ...TIME_LOG_STYLES.reportMetricIcon,
                              color: "#000000",
                              background: "#f5f5f5",
                            }}
                          >
                            <i className="fa fa-calendar" aria-hidden="true"></i>
                          </span>
                          <div style={TIME_LOG_STYLES.reportMetricLabel}>Month / Year</div>
                          <div style={TIME_LOG_STYLES.reportMetricValue}>{report.monthYear}</div>
                          <span style={TIME_LOG_STYLES.reportMuted}>Calendar period</span>
                        </div>
                        <div style={TIME_LOG_STYLES.reportMetricCard}>
                          <span
                            style={{
                              ...TIME_LOG_STYLES.reportMetricAccent,
                              background: "#0f766e",
                            }}
                          />
                          <span
                            style={{
                              ...TIME_LOG_STYLES.reportMetricIcon,
                              color: "#0f766e",
                              background: "#ecfdf5",
                            }}
                          >
                            <i className="fa fa-clock-o" aria-hidden="true"></i>
                          </span>
                          <div style={TIME_LOG_STYLES.reportMetricLabel}>Approved Hours</div>
                          <div style={TIME_LOG_STYLES.reportMetricValue}>{report.hoursLabel}</div>
                          <span style={TIME_LOG_STYLES.reportMuted}>
                            Logged and approved by client
                          </span>
                        </div>
                        <div style={TIME_LOG_STYLES.reportMetricCard}>
                          <span
                            style={{
                              ...TIME_LOG_STYLES.reportMetricAccent,
                              background: "#7c3aed",
                            }}
                          />
                          <span
                            style={{
                              ...TIME_LOG_STYLES.reportMetricIcon,
                              color: "#7c3aed",
                              background: "#f5f3ff",
                            }}
                          >
                            <i className="fa fa-usd" aria-hidden="true"></i>
                          </span>
                          <div style={TIME_LOG_STYLES.reportMetricLabel}>Rate / Hour</div>
                          <div style={TIME_LOG_STYLES.reportMetricValue}>
                            {formatCurrency(report.rate)}
                          </div>
                          <span style={TIME_LOG_STYLES.reportMuted}>Applicable hourly rate</span>
                        </div>
                        <div style={TIME_LOG_STYLES.reportMetricCard}>
                          <span
                            style={{
                              ...TIME_LOG_STYLES.reportMetricAccent,
                              background: "#15803d",
                            }}
                          />
                          <span
                            style={{
                              ...TIME_LOG_STYLES.reportMetricIcon,
                              color: "#15803d",
                              background: "#f0fdf4",
                            }}
                          >
                            <i className="fa fa-credit-card" aria-hidden="true"></i>
                          </span>
                          <div style={TIME_LOG_STYLES.reportMetricLabel}>Total Compensation</div>
                          <div style={TIME_LOG_STYLES.reportMetricValue}>
                            {formatCurrency(report.totalCompensation)}
                          </div>
                          <span style={TIME_LOG_STYLES.reportMuted}>Less applicable Taxes</span>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={TIME_LOG_STYLES.modalFooter}>
          <div style={TIME_LOG_STYLES.modalFooterActions}>
            <button
              type="button"
              className="btn btn-sm mr-2"
              style={{
                ...TIME_LOG_STYLES.modalPrimaryButton,
                ...(reportPdfLoading
                  ? TIME_LOG_STYLES.modalPrimaryButtonDisabled
                  : {}),
              }}
              onClick={generateTimeLogReportPdf}
              disabled={reportPdfLoading}
            >
              <i
                className={`fa ${reportPdfLoading
                  ? "fa-spinner fa-spin"
                  : isTimeLogReportGenerated(reportTimeLogObj)
                    ? "fa-external-link"
                    : "fa-file-pdf-o"
                  }`}
                aria-hidden="true"
              ></i>
              {reportPdfLoading
                ? "Preparing PDF..."
                : isTimeLogReportGenerated(reportTimeLogObj)
                  ? "Preview PDF"
                  : "Generate PDF"}
            </button>
            <button
              type="button"
              className="btn btn-sm"
              style={TIME_LOG_STYLES.subtleButton}
              onClick={() => handleReportTimeLog(null)}
            >
              <i className="fa fa-times" aria-hidden="true"></i> Close
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* <AddTimeLog
        showHideTimeLog={showHideTimeLog}
        handleShowHideTimeLog={handleShowHideTimeLog}
        appTasklist={appTasklist}
        workflowTask={workflowTask}
      ></AddTimeLog> */}
    </React.Fragment>
  );
}
